(ns ui.enrich.existing
  "AI enrichment of an existing entity from one of its attachments.

   The attachments card starts a job that advertises every value field the user
   can currently write on the entity (custom fields included). The job runs in
   the background like the create flows, and the global enrich popup offers to
   review the result. Reviewing opens a comparison modal: one row per field
   where the AI value differs from the current one, current value on the left,
   AI value on the right, AI value preselected. Save writes the chosen AI values
   in one update; Discard drops the result.

   The entity id travels in the request's `:draft`, so a result names the entity
   it belongs to and the popup can tell it apart from a create-flow result."
  (:require
   [clojure.string :as str]
   [common.localization :as loc]
   [common.schema.entities.pipeline :as pipeline]
   [common.schema.registry :as registry]
   [frontend.actions.entity :as entity-actions]
   [frontend.logging :as logging]
   [frontend.state.current-session :as session]
   [frontend.state.permissions :as f-permissions]
   [frontend.updates.entity-scope :as entity-scope]
   [potpuri.core :as p]
   [re-frame.core :as rf]
   [reagent.core :as r]
   [ui.components.alert.view :as alert]
   [ui.components.breadcrumbs.view :as breadcrumbs]
   [ui.components.button.view :as button]
   [ui.components.drawer.view :as drawer]
   [ui.components.loading-spinner.view :as spinner]
   [ui.components.toggle.view :as toggle]
   [ui.dom-globals :as dg]
   [ui.icons :as icons]
   [ui.enrich.apply :as enrich-apply]
   [ui.enrich.event :as enrich-event]
   [ui.enrich.model :as enrich-model]
   [ui.enrich.sub :as enrich-sub]
   [ui.state.session.sub :as session-sub]
   [ui.util :as uu]
   [ui.view.card.sub :as card-sub]
   [ui.view.enrich.fly :as fly]))

;; ---------------------------------------------------------------------------
;; Field set and request (pure)
;; ---------------------------------------------------------------------------

(def non-value-data-types
  "Data types not advertised: references need an id, attachment slots a
   document, and a currency symbol is a companion of its amount that
   ui.enrich.apply cannot clean."
  #{:data.type/ref-single :data.type/ref-multi :data.type/attachment-slot
    :data.type/currency-symbol})

(defn candidate-fields
  "The value fields of `entity-type` the AI could fill, before permissions: no
   system or shared `:common/*` fields (created-on, external id, network drives,
   folders), references, attachment slots, or calculated or artificial fields.
   Sorted, so the request is stable."
  [tenant-schema entity-type]
  (->> (registry/get-schema tenant-schema entity-type)
       (remove (fn [[field field-schema]]
                 (or (= "common" (namespace field))
                     (contains? card-sub/hidden-peek-entity-fields field)
                     (contains? non-value-data-types (:field/data-type field-schema))
                     (:field/calculated-field field-schema)
                     (:field/artificial? field-schema))))
       (map first)
       sort
       vec))

(defn writable-fields
  "The candidate fields of `entity` that `write-access?` allows.
   `write-access?` takes field, entity type and pipeline id."
  [tenant-schema entity write-access?]
  (let [entity-type (:entity/type entity)
        pipeline-id (pipeline/pipeline entity)]
    (filterv #(write-access? % entity-type pipeline-id)
             (candidate-fields tenant-schema entity-type))))

(defn request-params
  "`::ui.enrich.event/request` params for filling `fields` of `entity` from one
   attachment."
  [entity fields attachment-id]
  (let [entity-type (:entity/type entity)]
    {:entity-type    entity-type
     :targets        [{:entity-type entity-type :fields fields}]
     :attachment-ids [attachment-id]
     :draft          {:entity-id (:db/id entity)}}))

;; ---------------------------------------------------------------------------
;; Comparison (pure)
;; ---------------------------------------------------------------------------

(defn display-text
  "A materialized field value as one line of text, nil when empty."
  [materialized]
  (let [text (cond
               (nil? materialized)
               nil

               (sequential? materialized)
               (->> materialized
                    (map #(if (map? %) (:label %) %))
                    (remove nil?)
                    (str/join ", "))

               :else
               (str materialized))]
    (when-not (str/blank? text)
      text)))

(defn comparison-rows
  "One row per suggested field whose AI value reads differently from the current
   one: {:field :label :current :suggested}, sorted by label. `suggested` is the
   cleaned {field value} map; `text-of` turns a field, a value and the record
   it is read in into display text (nil when empty); `label-of` names a field;
   `currency-fields` are the fields holding an amount's currency. Those get no
   row of their own: an amount reads in its currency, the AI amount in the AI
   currency."
  [{:keys [suggested entity text-of label-of currency-fields]}]
  (let [suggested-record (merge entity suggested)]
    (->> (apply dissoc suggested currency-fields)
         (keep (fn [[field value]]
                 (let [current   (text-of field (get entity field) entity)
                       suggested (text-of field value suggested-record)]
                   (when (and suggested (not= current suggested))
                     {:field     field
                      :label     (label-of field)
                      :current   current
                      :suggested suggested}))))
         (sort-by :label)
         vec)))

(defn chosen-updates
  "The {field value} update for the rows where the user kept the AI value: an
   AI amount brings its AI currency along."
  [suggested ai-fields currency-fields]
  (let [currencies (filter #(contains? ai-fields (enrich-apply/amount-of %)) currency-fields)]
    (select-keys suggested (concat ai-fields currencies))))

;; ---------------------------------------------------------------------------
;; State and events
;; ---------------------------------------------------------------------------

(def state-path
  "The open review: {:result-id :entity-id :load-id :entity :entities :saving?
   :error}. The load id names one opening of a review: a load and a save
   carry it, and change the state only while that opening is the current one."
  [::state])

(rf/reg-sub ::state (fn [db _] (get-in db state-path)))

(defn- for-load
  "`f` applied to the review state when `load-id` is its current load: a late
   reply of an earlier load, also one of the same result reviewed again, is
   dropped."
  [db load-id f]
  (if (= load-id (get-in db (conj state-path :load-id)))
    (update-in db state-path f)
    db))

(rf/reg-fx
 ::fetch-entity
 (fn [{:keys [load-id entity-id]}]
   (entity-scope/load-entity!
    {:entity-id entity-id
     :on-loaded (fn [entity entities]
                  (rf/dispatch [::entity-loaded load-id entity entities]))
     :on-error  (fn [error]
                  (rf/dispatch [::entity-load-failed load-id entity-id error]))})))

(rf/reg-event-fx
 ::load-entity
 (fn [{:keys [db]} [_ result-id entity-id]]
   (let [load-id (str (random-uuid))]
     {:db            (assoc-in db state-path {:result-id result-id
                                              :entity-id entity-id
                                              :load-id   load-id})
      ::fetch-entity {:load-id load-id :entity-id entity-id}})))

(defn entity-loaded
  [db [_ load-id entity entities]]
  (for-load db load-id #(assoc % :entity entity :entities (p/index-by :db/id entities))))

(defn entity-load-failed
  [db [_ load-id entity-id error]]
  (logging/error "enrich: could not load entity" entity-id error)
  (for-load db load-id #(assoc % :error :load-failed)))

(rf/reg-event-db ::entity-loaded entity-loaded)
(rf/reg-event-db ::entity-load-failed entity-load-failed)

(rf/reg-fx
 ::update-entity
 (fn [{:keys [load-id entity-id updates on-saved]}]
   (entity-actions/*->entities-update!
    [{:db/id entity-id :data updates}]
    (fn [_]
      (rf/dispatch [::saved load-id])
      (on-saved))
    (fn [err]
      (logging/error "enrich: could not save AI values" err)
      (rf/dispatch [::save-failed load-id])))))

(rf/reg-event-fx
 ::save
 (fn [{:keys [db]} [_ result-id updates on-saved]]
   (let [{:keys [entity-id load-id] :as state} (get-in db state-path)]
     (if (and entity-id (= result-id (:result-id state)))
       {:db (update-in db state-path assoc :saving? true :error nil)
        :fx [[::update-entity {:load-id   load-id
                               :entity-id entity-id
                               :updates   updates
                               :on-saved  on-saved}]]}
       {}))))

;; A save's reply changes the review only while the review it was made in is
;; open: the same result reviewed again meanwhile is another review.
(defn saved
  [db [_ load-id]]
  (if (= load-id (get-in db (conj state-path :load-id)))
    (assoc-in db state-path nil)
    db))

(defn save-failed
  [db [_ load-id]]
  (for-load db load-id #(assoc % :saving? false :error :save-failed)))

(rf/reg-event-db ::saved saved)
(rf/reg-event-db ::save-failed save-failed)

(rf/reg-event-db
 ::clear
 (fn [db [_ result-id]]
   (cond-> db
     (= result-id (get-in db (conj state-path :result-id)))
     (assoc-in state-path nil))))

;; The entities robot buttons start from, loaded by id: {entity-id {:entity
;; :loading?}}. A screen whose rows only know the entity's id shares one load
;; per entity; a later mount loads it again, so the fields follow its pipeline.
(def targets-path [::targets])

(rf/reg-sub ::target (fn [db [_ entity-id]] (get-in db (conj targets-path entity-id :entity))))

(rf/reg-fx
 ::fetch-target
 (fn [entity-id]
   (entity-scope/load-entity!
    {:entity-id entity-id
     :on-loaded (fn [entity _] (rf/dispatch [::target-loaded entity-id entity]))
     :on-error  (fn [error] (rf/dispatch [::target-load-failed entity-id error]))})))

(rf/reg-event-fx
 ::load-target
 (fn [{:keys [db]} [_ entity-id]]
   (if (get-in db (conj targets-path entity-id :loading?))
     {}
     {:db            (assoc-in db (conj targets-path entity-id :loading?) true)
      ::fetch-target entity-id})))

(rf/reg-event-db
 ::target-loaded
 (fn [db [_ entity-id entity]]
   (assoc-in db (conj targets-path entity-id) {:entity entity})))

;; Without its entity the robot cannot tell which fields it could fill, so it
;; stays hidden.
(rf/reg-event-db
 ::target-load-failed
 (fn [db [_ entity-id error]]
   (logging/error "enrich: could not load the entity for the robot" entity-id error)
   (assoc-in db (conj targets-path entity-id) {})))

;; ---------------------------------------------------------------------------
;; Views
;; ---------------------------------------------------------------------------

(defn start-button
  "The robot tool on an attachment of `entity`, shown when the user may run AI
   enrichment and can write at least one field it could fill. Disabled while a
   job for the same entity runs, since two reviews of one entity would compete;
   jobs for other entities may run meanwhile."
  [{:keys [entity attachment-id]}]
  (let [in-progress?  @(rf/subscribe [::enrich-sub/entity-in-progress? (:db/id entity)])
        tenant-schema @(rf/subscribe [::session-sub/tenant-schema])
        fields        (when entity
                        (writable-fields tenant-schema entity f-permissions/field-write-access?))]
    (when (and (seq fields)
               (not (session/is-read-only-user?))
               (session/has-feature-access? :feature/ai-enrichment))
      [button/square-secondary-open
       {:tooltip   (loc/tr (if in-progress?
                             :enrich-existing/in-progress
                             :enrich-existing/start))
        :disabled? in-progress?
        :on-click  (fn [e]
                     (let [token (enrich-event/new-token)]
                       (rf/dispatch [::enrich-event/request
                                     (assoc (request-params entity fields attachment-id)
                                            :token token)])
                       (fly/to-processes! (dg/current-target e) token)))}
       [icons/sparkles-icon {:size 16}]])))

(defn start-button-for
  "`start-button` for a screen that knows only the entity's id: loads the
   entity from the backend, then shows the robot. A view that stays mounted
   while its entity changes loads the new one."
  [_props]
  (r/create-class
   {:component-did-mount
    (fn [this]
      (rf/dispatch [::load-target (:entity-id (r/props this))]))
    :component-did-update
    (fn [this [_ old-props]]
      (let [entity-id (:entity-id (r/props this))]
        (when (not= entity-id (:entity-id old-props))
          (rf/dispatch [::load-target entity-id]))))
    :reagent-render
    (fn [{:keys [entity-id attachment-id]}]
      (when-let [entity @(rf/subscribe [::target entity-id])]
        [start-button {:entity entity :attachment-id attachment-id}]))}))

(defn- ai-suggestion-block
  "The AI suggestion of a field: the sparkle mark, a toggle that keeps the AI
   value, and the suggested text."
  [{:keys [suggested ai? on-toggle]}]
  [:div {:class "flex flex-col gap-2 rounded-lg border border-solid p-3
                 tw2-border-card-border-selected tw2-bg-card-bg-selected"}
   [:div {:class "flex items-center justify-between gap-2"}
    [:div {:class "flex items-center gap-2"}
     [icons/sparkles-icon {:size 16 :class "text-success-regular"}]
     [:span {:class "text-sm font-medium"} (loc/tr :enrich-existing/ai-suggestion)]]
    [:div {:class "flex items-center gap-2"}
     [toggle/toggle {:checked?   ai?
                     :on-change  on-toggle
                     :aria-label (loc/tr :enrich-existing/use-ai)}]
     [:span {:class "text-xs text-base-light-text-paler"} (loc/tr :enrich-existing/use-ai)]]]
   [:div {:class "text-sm break-words whitespace-pre-line"} suggested]])

(defn- field-card
  "One field under review: its name, the current value and the AI suggestion,
   stacked, or side by side in the wide view."
  [{:keys [row ai? on-toggle wide?]}]
  (let [{:keys [label current suggested]} row]
    [:div {:class "flex flex-col gap-3 rounded-lg border border-solid border-base-light-border-paler p-4"}
     [:div {:class "text-base font-semibold break-words"} label]
     [:div {:class (if wide?
                     "grid grid-cols-2 gap-4 items-start"
                     "flex flex-col gap-3")}
      [:div {:class (uu/cm "text-sm break-words whitespace-pre-line"
                           (when-not current "text-base-light-text-paler"))}
       (or current (loc/tr :enrich-existing/appears-empty))]
      [ai-suggestion-block {:suggested suggested
                            :ai?       ai?
                            :on-toggle on-toggle}]]]))

(defn review-curtain
  "Review an existing-entity `result` against the entity's current values,
   loaded from the backend (entity-scope/load-entity!) on every review: a
   session copy may be stale or partial, and Apply writes over what the cards
   show. The cards are fixed once the entity is loaded, and every field starts
   on the AI value. `on-resolved` runs after Apply or Discard, which are done
   with the result; `on-close` runs when the curtain is closed otherwise, which
   keeps the result for later. The wide view puts the current value and the AI
   suggestion side by side. Mount it keyed by the result id."
  [{:keys [result]}]
  (let [result-id (:result-id result)
        ;; Fields whose AI value is kept; nil until the cards are known.
        ai-fields (r/atom nil)
        wide?     (r/atom false)]
    (rf/dispatch [::load-entity result-id (enrich-model/job-entity-id result)])
    (fn [{:keys [result on-close on-resolved]}]
      (let [state         (let [state @(rf/subscribe [::state])]
                            ;; Another review's state is none of this one's.
                            (when (= result-id (:result-id state)) state))
            {:keys [error saving?]} state
            entity        (:entity state)
            ;; Only what the backend load returned: the session's copies may
            ;; be stale (ai/frontend-data-loading.md).
            entities      (:entities state)
            tenant        @(rf/subscribe [::session-sub/tenant])
            tenant-schema @(rf/subscribe [::session-sub/tenant-schema])
            suggested     (enrich-apply/entity-initial-values result tenant-schema)
            currency-fields (->> (keys suggested)
                                 (filter #(= :data.type/currency-symbol
                                             (:field/data-type
                                              (registry/get-field-schema tenant-schema %))))
                                 set)
            text-of       (fn [field value record]
                            (display-text
                             (uu/field-value->materialized-value
                              {:field          field
                               :value          value
                               :entity         record
                               :id->entity     entities
                               :tenant         tenant
                               :tenant-schema  tenant-schema
                               :show-currency? true})))
            label-of      (fn [field]
                            (loc/field-name field (registry/get-field-schema tenant-schema field)))
            rows          (when entity
                            (comparison-rows {:suggested       suggested
                                              :entity          entity
                                              :text-of         text-of
                                              :label-of        label-of
                                              :currency-fields currency-fields}))
            _             (when (and rows (nil? @ai-fields))
                            (reset! ai-fields (set (map :field rows))))
            updates       (chosen-updates suggested @ai-fields currency-fields)
            close!        (fn []
                            (rf/dispatch [::clear result-id])
                            (on-close))
            back!         (fn []
                            (close!)
                            (rf/dispatch [::enrich-event/set-menu-open true]))
            discard!      (fn []
                            (rf/dispatch [::clear result-id])
                            (on-resolved))
            save!         #(rf/dispatch [::save result-id updates on-resolved])]
        [drawer/view
         {:visible?     true
          :hide!        close!
          :size         (if @wide? :lg :md)
          :header-child [:div {:class "flex w-full items-center justify-between gap-2"}
                         [breadcrumbs/view
                          {:items [{:label    (loc/tr :enrich-process/title)
                                    :on-click back!}
                                   {:label   (or (:entity-name result)
                                                 (loc/tr :enrich-existing/title))
                                    :active? true}]}]
                         [:div {:class "flex items-center gap-1"}
                          [button/component
                           {:variant    :ghost
                            :size       :sm
                            :aria-label (loc/tr :enrich-existing/close)
                            :on-click   close!}
                           [icons/x-icon {:size 20}]]]]
          :body         [:div {:class "flex flex-col gap-4 py-2"}
                         (case error
                           :load-failed [alert/danger {} (loc/tr :enrich-existing/load-failed)]
                           :save-failed [alert/danger {} (loc/tr :enrich-existing/save-failed)]
                           nil)
                         (cond
                           (and (nil? entity) (not error))
                           [:div {:class "flex justify-center py-6"} [spinner/circle]]

                           (and entity (empty? rows))
                           [alert/info {} (loc/tr :enrich-existing/no-changes)]

                           entity
                           (doall
                            (for [{:keys [field] :as row} rows]
                              ^{:key field}
                              [field-card {:row       row
                                           :ai?       (contains? @ai-fields field)
                                           :on-toggle (fn [use-ai?]
                                                        (swap! ai-fields (if use-ai? conj disj) field))
                                           :wide?     @wide?}])))]
          :footer       [:div {:class "flex w-full items-center justify-between gap-2"}
                         [button/secondary-hollow
                          {:on-click discard!}
                          (loc/tr :enrich-existing/discard)]
                         [button/primary-solid
                          {:disabled? (or saving? (empty? updates))
                           :on-click  save!}
                          (loc/tr :enrich-existing/apply)]]}]))))
